import {DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewContainerRef, ViewEncapsulation} from '@angular/core';
import {
    SavedCartDetailsOverviewComponent,
    SavedCartDetailsService
} from '@spartacus/cart/saved-cart/components';
import {GlobalMessageService, GlobalMessageType, TranslationService, User} from '@spartacus/core';
import {Card, LAUNCH_CALLER, LaunchDialogService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {Observable, Subscription} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import {UigCartService} from '../../../uig-cart.service';
import {Cart} from "@spartacus/cart/base/root";

@Component({
    selector: 'app-uig-saved-cart-details-overview',
    templateUrl: './uig-saved-cart-details-overview.component.html',
    styleUrls: ['./uig-saved-cart-details-overview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class UigSavedCartDetailsOverviewComponent extends SavedCartDetailsOverviewComponent implements OnInit, OnDestroy {
  private dialogSubscription = new Subscription();
  readonly datePattern = 'dd.MM.yyyy';
  publicCart: boolean;
  isOwner: boolean;
  cartId: string;
  updatePublicCartFlag$: Observable<Object>;
  isRestricted: boolean = false;
  RIO_CART_ORDER: string = "rio_cart_order";
  RIO_CART_CREATE_UPDATE: string = "rio_cart_create_update";
  private userSubscription = new Subscription();

  constructor(
    savedCartDetailsService: SavedCartDetailsService,
    translation: TranslationService,
    vcr: ViewContainerRef,
    private uigCartService: UigCartService,
    private globalMessageService: GlobalMessageService,
    private datepipe: DatePipe,
    private userAccountFacade: UserAccountFacade,
    launchDialogService: LaunchDialogService
  ) {
    super(savedCartDetailsService, translation, vcr, launchDialogService);
  }

  ngOnInit(): void {
    this.checkForRoles();
    this.savedCart$.pipe(
      filter((cart) => cart != null),
      tap((cart) => {
        this.publicCart = cart.publicCart;
        this.isOwner = cart.isOwner;
        this.cartId = cart.code;
      })
    )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.dialogSubscription?.unsubscribe();
  }

  getCartName(cartName: string): Observable<Card> {
    if (!cartName) {
      cartName = " - ";
    }
    return this.translation.translate('customSavedCartDetails.cartName').pipe(
      filter(() => Boolean(cartName)),
      map((textTitle) => ({
        title: textTitle,
        text: [cartName],
      }))
    );
  }

  getCartDescription(cartDescription: string): Observable<Card> {
    if (!cartDescription) {
      cartDescription = " - ";
    }
    return this.translation.translate('customSavedCartDetails.cartDescription').pipe(
      filter(() => Boolean(cartDescription)),
      map((textTitle) => ({
        title: textTitle,
        text: [cartDescription],
      }))
    );
  }

  getCartId(cartId: string): Observable<Card> {
    return this.translation.translate('customSavedCartDetails.cartId').pipe(
      filter(() => Boolean(cartId)),
      map((textTitle) => ({
        title: textTitle,
        text: [cartId],
      }))
    );
  }

  getDateSaved(saveTime: string): Observable<Card> {
    return this.translation.translate('customSavedCartDetails.dateSaved').pipe(
      filter(() => Boolean(saveTime)),
      map((textTitle) => {
        const date = this.datepipe.transform(new Date(saveTime), this.datePattern);

        return {
          title: textTitle,
          text: [date],
        };
      })
    );
  }

  getCartItems(totalItems: number): Observable<Card> {
    return this.translation.translate('customSavedCartDetails.items').pipe(
      filter(() => Boolean(totalItems)),
      map((textTitle) => ({
        title: textTitle,
        text: [totalItems.toString()],
      }))
    );
  }

  getCartQuantity(totalUnitCount: number): Observable<Card> {
    return this.translation.translate('customSavedCartDetails.quantity').pipe(
      filter(() => Boolean(totalUnitCount)),
      map((textTitle) => ({
        title: textTitle,
        text: [totalUnitCount.toString()],
      }))
    );
  }

  getCartTotal(totalPriceWithTax: string): Observable<Card> {
    return this.translation.translate('customSavedCartDetails.total').pipe(
      filter(() => Boolean(totalPriceWithTax)),
      map((textTitle) => ({
        title: textTitle,
        text: [totalPriceWithTax],
      }))
    );
  }

  openDialog(cart: Cart): void {
    const dialog = this.launchDialogService.openDialog(
      LAUNCH_CALLER.SAVED_CART,
      this.element,
      this.vcr,
      { cart, layoutOption: 'save' }
    );
    if (dialog) {
      this.dialogSubscription.add(dialog.pipe(take(1)).subscribe());
    }
  }

  private formatDate(givenDate: Date): string {
    const date = givenDate.toDateString().split(' ');

    const month = date[1];
    const day = date[2];
    const year = date[3];

    return month + ' ' + day + ' ' + year;
  }

  onChangePublicFlag(): void {
    this.updatePublicCartFlag$ = this.uigCartService.updatePublicCartFlag(this.publicCart, this.cartId);
    this.updatePublicCartFlag$
      .pipe(
        tap(
          (success) => {
            if (success == true) {
              this.translation
                .translate('customSavedCartDetails.publicCartFlagUpdated')
                .pipe(take(1))
                .subscribe((message) => {
                  this.globalMessageService.add(
                    message,
                    GlobalMessageType.MSG_TYPE_CONFIRMATION
                  );
                });
            } else {
              this.translation
                .translate('customSavedCartDetails.publicCartFlagError')
                .pipe(take(1))
                .subscribe((message) => {
                  this.globalMessageService.add(
                    message,
                    GlobalMessageType.MSG_TYPE_ERROR
                  );
                });
            }
          }
        )
      )
      .subscribe();
  }

  checkForRoles() {
    this.userSubscription = this.userAccountFacade
      .get()
      .pipe(filter((user: User) => user && Object.keys(user).length > 0))
      .subscribe((user) => {
        const hasRole =
          user.roles && (user.roles.includes(this.RIO_CART_CREATE_UPDATE)
            || user.roles.includes(this.RIO_CART_ORDER));
        if (!hasRole) {
          this.isRestricted = true;
        }
      });
  }
}
